import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';

const Container = styled.div<{ $embedded?: boolean }>`
  max-width: 1800px;
  margin: 0 auto;
  padding: ${props => props.$embedded ? '1rem' : '2rem'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    color: white;
    border: none;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4); }
  ` : `
    background: transparent;
    color: #94a3b8;
    border: 1px solid rgba(255, 255, 255, 0.1);
    &:hover { border-color: #8b5cf6; color: white; }
  `}
`;

const ExposureSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ExposureCard = styled.div<{ $type: 'bought' | 'sold' | 'net' }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  border-top: 4px solid ${props => {
    switch (props.$type) {
      case 'bought': return '#ef4444';
      case 'sold': return '#10b981';
      case 'net': return '#8b5cf6';
    }
  }};
`;

const ExposureLabel = styled.div`
  color: #94a3b8;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const ExposureValue = styled.div<{ $positive?: boolean }>`
  color: ${props => props.$positive !== undefined ? (props.$positive ? '#10b981' : '#ef4444') : 'white'};
  font-size: 2rem;
  font-weight: 700;
`;

const ExposureSubtext = styled.div`
  color: #64748b;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const SectionTitle = styled.h3`
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TabsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? 'rgba(139, 92, 246, 0.2)' : 'transparent'};
  border: 1px solid ${props => props.$active ? '#8b5cf6' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$active ? 'white' : '#94a3b8'};

  &:hover {
    border-color: #8b5cf6;
    color: white;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(30, 41, 59, 0.5);
  border-radius: 12px;
  overflow: hidden;
`;

const Th = styled.th`
  padding: 1rem;
  text-align: left;
  background: rgba(30, 41, 59, 0.8);
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Td = styled.td`
  padding: 1rem;
  color: white;
  font-size: 0.875rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const DirectionBadge = styled.span<{ $direction: string }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;

  ${props => props.$direction === 'BUY_PROTECTION' ? `
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.2);
  ` : `
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
    border: 1px solid rgba(16, 185, 129, 0.2);
  `}
`;

const TypeBadge = styled.span<{ $type: string }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  background: rgba(139, 92, 246, 0.1);
  color: #8b5cf6;
  border: 1px solid rgba(139, 92, 246, 0.2);
`;

const SpreadIndicator = styled.span<{ $tight?: boolean }>`
  color: ${props => props.$tight ? '#10b981' : 'white'};
  font-weight: 600;
`;

interface CDSContract {
  id: number;
  referenceEntity?: string;
  referenceEntityName?: string;
  contractType: string;
  direction: string;
  notionalAmount: number;
  spread?: number;
  fixedRateBps?: number;
  currentSpreadBps?: number;
  upfront?: number;
  upfrontPct?: number;
  effectiveDate: string;
  maturityDate: string;
  counterparty: any;
  isCleared: boolean;
  indexSeries?: number;
  indexVersion?: number;
}

interface CDSTradingScreenProps {
  embedded?: boolean;
}

const CDSTradingScreen: React.FC<CDSTradingScreenProps> = ({ embedded = false }) => {
  const [contracts, setContracts] = useState<CDSContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'single-name' | 'index'>('all');

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/credit/cds');
        setContracts(response.data);
      } catch (error) {
        console.error('Failed to fetch CDS contracts:', error);
        // A failed load must render as a load error, never as a plausible book.
        setContracts([]);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchContracts();
  }, []);

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(2)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
    return `$${value.toLocaleString()}`;
  };

  const filteredContracts = contracts.filter(c => {
    if (activeTab === 'all') return true;
    if (activeTab === 'single-name') return c.contractType === 'SINGLE_NAME';
    if (activeTab === 'index') return c.contractType === 'INDEX';
    return true;
  });

  const protectionBought = contracts.filter(c => c.direction === 'BUY_PROTECTION').reduce((sum, c) => sum + c.notionalAmount, 0);
  const protectionSold = contracts.filter(c => c.direction === 'SELL_PROTECTION').reduce((sum, c) => sum + c.notionalAmount, 0);
  const netExposure = protectionSold - protectionBought;

  return (
    <Container $embedded={embedded}>
      {loadError && (
        <div style={{ margin: '0 0 1rem', padding: '0.6rem 0.9rem', borderRadius: 8, border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: '0.8rem' }}>
          ⚠ Couldn't load CDS contracts — the request failed. This is a load error, not an empty book.
        </div>
      )}
      {!embedded && (
        <Header>
          <Title>
            <span>🔄</span>
            CDS Trading
          </Title>
          <Actions>
            <Button $variant="secondary">Export</Button>
            <Button $variant="primary">+ New Contract</Button>
          </Actions>
        </Header>
      )}

      <ExposureSummary>
        <ExposureCard $type="bought">
          <ExposureLabel>Protection Bought</ExposureLabel>
          <ExposureValue>{formatCurrency(protectionBought)}</ExposureValue>
          <ExposureSubtext>{contracts.filter(c => c.direction === 'BUY_PROTECTION').length} contracts</ExposureSubtext>
        </ExposureCard>
        <ExposureCard $type="sold">
          <ExposureLabel>Protection Sold</ExposureLabel>
          <ExposureValue>{formatCurrency(protectionSold)}</ExposureValue>
          <ExposureSubtext>{contracts.filter(c => c.direction === 'SELL_PROTECTION').length} contracts</ExposureSubtext>
        </ExposureCard>
        <ExposureCard $type="net">
          <ExposureLabel>Net Exposure</ExposureLabel>
          <ExposureValue $positive={netExposure >= 0}>{formatCurrency(Math.abs(netExposure))}</ExposureValue>
          <ExposureSubtext>{netExposure >= 0 ? 'Net seller of protection' : 'Net buyer of protection'}</ExposureSubtext>
        </ExposureCard>
      </ExposureSummary>

      <SectionTitle>
        <span>📋</span>
        Active Contracts
      </SectionTitle>

      <TabsRow>
        <TabButton $active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
          All ({contracts.length})
        </TabButton>
        <TabButton $active={activeTab === 'single-name'} onClick={() => setActiveTab('single-name')}>
          Single-Name ({contracts.filter(c => c.contractType === 'SINGLE_NAME').length})
        </TabButton>
        <TabButton $active={activeTab === 'index'} onClick={() => setActiveTab('index')}>
          Index ({contracts.filter(c => c.contractType === 'INDEX').length})
        </TabButton>
      </TabsRow>

      <Table>
        <thead>
          <tr>
            <Th>Reference Entity</Th>
            <Th>Type</Th>
            <Th>Direction</Th>
            <Th>Notional</Th>
            <Th>Spread</Th>
            <Th>Upfront</Th>
            <Th>Maturity</Th>
            <Th>Counterparty</Th>
            <Th>Cleared</Th>
          </tr>
        </thead>
        <tbody>
          {filteredContracts.map(contract => {
            const refEntity = contract.referenceEntityName || contract.referenceEntity || '-';
            const spread = contract.currentSpreadBps ?? contract.fixedRateBps ?? contract.spread ?? 0;
            const upfront = contract.upfrontPct ?? contract.upfront ?? 0;
            const cpName = typeof contract.counterparty === 'string'
              ? contract.counterparty
              : contract.counterparty?.counterpartyName || contract.counterparty?.name || '-';
            return (
              <tr key={contract.id}>
                <Td style={{ fontWeight: 600 }}>{refEntity}</Td>
                <Td><TypeBadge $type={contract.contractType}>{(contract.contractType || 'UNKNOWN').replace('_', ' ')}</TypeBadge></Td>
                <Td><DirectionBadge $direction={contract.direction}>{(contract.direction || '') === 'BUY_PROTECTION' ? 'Buy' : 'Sell'}</DirectionBadge></Td>
                <Td>{formatCurrency(contract.notionalAmount)}</Td>
                <Td><SpreadIndicator $tight={spread < 100}>{spread}bp</SpreadIndicator></Td>
                <Td>{upfront > 0 ? `${upfront}%` : '-'}</Td>
                <Td>{new Date(contract.maturityDate).toLocaleDateString()}</Td>
                <Td>{cpName}</Td>
                <Td>{contract.isCleared ? '✓' : '✗'}</Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Container>
  );
};

export default CDSTradingScreen;
